import React from 'react';
import { ReviewProfileCheckpoint } from '../../../api/structs/reviewProfile';
import { usePromptTemplates } from '../../../hooks/usePromptTemplates';
import { detailChip, detailHeading, detailMuted } from './reviewerDetailStyles';

const promptPre =
  'max-h-56 overflow-auto whitespace-pre-wrap border border-[var(--border-strong)] bg-[var(--surface-2)] p-2 font-mono text-[11px] leading-relaxed text-[var(--app-text)]';

/**
 * The judge's prompt, READ-ONLY, and the `{{placeholders}}` it asks for.
 *
 * Read-only on purpose: a judge template is edited in ONE place (Prompt
 * templates), and a second editable copy here would be a second answer to
 * "what does the judge read". The chips are what this surface adds — which
 * source fills each placeholder, and whether the engine writes anything into
 * it today, which the prompt text alone cannot say.
 */
const ReviewerPromptSection: React.FC<{ checkpoint: ReviewProfileCheckpoint }> = ({
  checkpoint,
}) => {
  const { templates, isLoading } = usePromptTemplates();
  const [fullscreen, setFullscreen] = React.useState(false);

  // Escape closes THIS overlay and stops there. The surface that opened the
  // graph listens for Escape on `window` too, so without swallowing the event
  // one keypress would shut both — the innermost thing a person can see is the
  // one they mean. A CAPTURE listener runs before any bubble-phase one, which
  // is what makes stopping it possible at all.
  React.useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.stopImmediatePropagation();
      setFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [fullscreen]);

  const template = templates.find((candidate) => candidate.id === checkpoint.judge_template_id);
  const content = template?.content || '';

  const body = (
    <pre className={promptPre} data-testid="reviewer-prompt-content">
      {content ||
        (isLoading
          ? 'Reading the judge template…'
          : 'This judge template is not in your prompt templates.')}
    </pre>
  );

  return (
    <section className="space-y-1.5" data-testid="reviewer-detail-prompt">
      <div className="flex items-center gap-1.5">
        <h4 className={detailHeading}>Prompt</h4>
        <span className={`${detailMuted} truncate`} title={checkpoint.judge_template_id}>
          {checkpoint.judge_template_id}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-ghost ml-auto font-mono text-[10px]"
          data-testid="reviewer-prompt-fullscreen"
          title="Read it full screen"
          aria-label="Read the judge prompt full screen"
          onClick={() => setFullscreen(true)}
        >
          ⛶
        </button>
      </div>

      {!checkpoint.judge_template_found && (
        <p className={detailMuted}>
          The engine could not resolve this template — the judge would run on its built-in default.
        </p>
      )}

      {body}

      <div className="flex flex-wrap gap-1">
        {checkpoint.slots.map((slot) => (
          <span
            key={slot.name}
            className={detailChip}
            data-testid={`reviewer-slot-chip-${slot.name}`}
            data-filled={slot.filled ? 'true' : 'false'}
            title={slot.note || `${slot.source} fills {{${slot.name}}}`}
          >
            <span className={slot.filled ? '' : 'line-through opacity-60'}>{`{{${slot.name}}}`}</span>
            {` · ${slot.source} · ${slot.filled ? 'filled' : 'empty'}`}
          </span>
        ))}
      </div>

      <p className={detailMuted}>Change in Prompt templates — this is a read-only copy.</p>

      {fullscreen && (
        <div
          className="fixed inset-0 z-[230] flex flex-col bg-black/95 p-4"
          data-testid="reviewer-prompt-overlay"
        >
          <div className="flex items-center gap-2 pb-2">
            <h4 className={detailHeading}>{checkpoint.judge_template_id}</h4>
            <button
              type="button"
              className="btn btn-sm btn-ghost ml-auto font-mono text-[10px]"
              data-testid="reviewer-prompt-fullscreen-exit"
              onClick={() => setFullscreen(false)}
            >
              ⊠ Close
            </button>
          </div>
          <pre className="flex-1 overflow-auto whitespace-pre-wrap border border-[var(--border-strong)] bg-[var(--surface-1)] p-3 font-mono text-[12px] leading-relaxed text-[var(--app-text)]">
            {content}
          </pre>
        </div>
      )}
    </section>
  );
};

export default ReviewerPromptSection;
